import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Check, ChevronRight, ChevronLeft, Upload, Copy, CheckCircle2 } from "lucide-react";
import api, { formatApiError, formatIDR } from "@/lib/api";
import AdSlot from "@/components/AdSlot";

const STEPS = ["Pilih Unit", "Jadwal", "Data Diri", "Pembayaran", "Selesai"];

export default function BookingPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [units, setUnits] = useState([]);
  const [settings, setSettings] = useState(null);
  const [step, setStep] = useState(0);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [date, setDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [duration, setDuration] = useState(1);
  const [bookedHours, setBookedHours] = useState([]);
  const [customerName, setCustomerName] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [notes, setNotes] = useState("");
  const [couponInput, setCouponInput] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null); // {code, discount, total_after}
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [booking, setBooking] = useState(null);
  const [proofFile, setProofFile] = useState(null);
  const [proofPreview, setProofPreview] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/units").then((r) => {
      setUnits(r.data);
      const uid = params.get("unit");
      if (uid) {
        const u = r.data.find((x) => x.id === uid);
        if (u) {
          setSelectedUnit(u);
          setStep(1);
        }
      }
    });
    api.get("/settings").then((r) => setSettings(r.data));
  }, []);

  useEffect(() => {
    if (selectedUnit && date) {
      api
        .get("/bookings/availability", { params: { unit_id: selectedUnit.id, booking_date: date } })
        .then((r) => setBookedHours(r.data.booked_hours || []));
    }
  }, [selectedUnit, date]);

  const totalPrice = selectedUnit ? selectedUnit.price_per_hour * duration : 0;
  const discount = appliedCoupon?.discount || 0;
  const totalAfterDiscount = Math.max(0, totalPrice - discount);

  const validateCoupon = async () => {
    if (!couponInput.trim()) {
      toast.error("Masukkan kode kupon");
      return;
    }
    if (!totalPrice) return;
    setValidatingCoupon(true);
    try {
      const { data } = await api.post("/coupons/validate", { code: couponInput.trim(), subtotal: totalPrice });
      setAppliedCoupon(data);
      toast.success(`Kupon ${data.code} berhasil digunakan!`);
    } catch (e) {
      setAppliedCoupon(null);
      toast.error(formatApiError(e.response?.data?.detail) || "Kupon tidak valid");
    } finally {
      setValidatingCoupon(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponInput("");
  };

  const canProceed = () => {
    if (step === 0) return !!selectedUnit;
    if (step === 1) return !!date && !!startTime && duration > 0;
    if (step === 2) return customerName.trim() && whatsapp.trim().length >= 8;
    return true;
  };

  const handleCreateBooking = async () => {
    setSubmitting(true);
    try {
      const wa = whatsapp.replace(/\D/g, "").replace(/^0/, "62");
      const { data } = await api.post("/bookings", {
        customer_name: customerName,
        whatsapp_number: wa,
        unit_id: selectedUnit.id,
        booking_date: date,
        start_time: startTime,
        duration_hours: duration,
        notes,
        coupon_code: appliedCoupon?.code || "",
      });
      setBooking(data);
      setStep(3);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Booking gagal");
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (f.size > 3 * 1024 * 1024) {
      toast.error("Ukuran file maksimal 3MB");
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setProofFile(f);
      setProofPreview(reader.result);
    };
    reader.readAsDataURL(f);
  };

  const handleUploadProof = async () => {
    if (!proofPreview) {
      toast.error("Pilih file bukti transfer dulu");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/bookings/${booking.id}/proof`, { payment_proof_base64: proofPreview });
      // Track booking conversion in GA4 (if configured)
      try {
        if (typeof window !== "undefined" && typeof window.trackBookingConversion === "function") {
          window.trackBookingConversion(booking.total_price, "IDR");
        }
      } catch (e) {}
      toast.success("Bukti pembayaran terkirim! Menunggu konfirmasi admin.");
      setStep(4);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const copy = (t) => {
    navigator.clipboard.writeText(t);
    toast.success("Disalin!");
  };

  return (
    <div className="pt-24 pb-20 min-h-screen" data-testid="booking-page">
      <div className="max-w-5xl mx-auto px-6 md:px-10">
        <div className="mb-10">
          <div className="text-xs uppercase tracking-widest text-[#00E5FF] mb-3">Booking Rental</div>
          <h1 className="font-heading text-3xl md:text-5xl font-black text-white tracking-tighter">
            AMANKAN SLOTMU.
          </h1>
        </div>

        {/* Top Booking Ad */}
        {settings && settings.ad_slot_booking_top && settings.ad_booking_top_enabled !== false && (
          <div className="mb-8 py-4 border-y border-white/5" data-testid="ad-booking-top-container">
            <AdSlot
              snippet={settings.ad_slot_booking_top}
              publisherId={settings.adsense_publisher_id}
              label="Sponsored"
              slotKey="booking_top"
            />
          </div>
        )}

        {/* Stepper */}
        <div className="mb-10 flex items-center overflow-x-auto pb-2" data-testid="booking-stepper">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center flex-shrink-0">
              <div className="flex items-center gap-3">
                <div
                  className={`w-8 h-8 flex items-center justify-center text-xs font-bold border transition-colors ${
                    step >= i
                      ? "bg-[#00E5FF] border-[#00E5FF] text-black"
                      : "bg-transparent border-white/20 text-gray-500"
                  }`}
                >
                  {step > i ? <Check size={14} /> : i + 1}
                </div>
                <div className={`text-xs uppercase tracking-widest whitespace-nowrap ${step >= i ? "text-white" : "text-gray-500"}`}>
                  {s}
                </div>
              </div>
              {i < STEPS.length - 1 && (
                <div className={`w-8 md:w-16 h-[1px] mx-3 ${step > i ? "bg-[#00E5FF]" : "bg-white/10"}`} />
              )}
            </div>
          ))}
        </div>

        <div className="border border-white/10 bg-[#0F0F11] p-6 md:p-10 min-h-[400px]">
          <AnimatePresence mode="wait">
            {step === 0 && (
              <motion.div key="s0" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <h3 className="font-heading text-2xl font-bold text-white mb-6">Pilih Unit Konsol</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {units.map((u) => (
                    <button
                      key={u.id}
                      data-testid={`select-unit-${u.id}`}
                      onClick={() => setSelectedUnit(u)}
                      className={`text-left p-5 border transition-colors ${
                        selectedUnit?.id === u.id
                          ? "border-[#00E5FF] bg-[#00E5FF]/5"
                          : "border-white/10 hover:border-white/30 bg-[#0A0A0B]"
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="px-2 py-1 bg-white/10 text-white text-[10px] font-black uppercase tracking-widest">
                          {u.console_type}
                        </div>
                        {selectedUnit?.id === u.id && <Check className="text-[#00E5FF]" size={18} />}
                      </div>
                      <div className="font-heading font-bold text-white mt-4">{u.name}</div>
                      <div className="text-sm text-gray-400 mt-1 line-clamp-2">{u.description}</div>
                      <div className="mt-4 pt-4 border-t border-white/5 text-[#00E5FF] font-bold">
                        {formatIDR(u.price_per_hour)}/jam
                      </div>
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {step === 1 && (
              <motion.div key="s1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <h3 className="font-heading text-2xl font-bold text-white mb-6">Pilih Tanggal & Jam</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-xs uppercase tracking-widest text-gray-400 font-semibold block mb-2">Tanggal</label>
                    <input
                      type="date"
                      value={date}
                      min={new Date().toISOString().split("T")[0]}
                      onChange={(e) => setDate(e.target.value)}
                      data-testid="booking-date-input"
                      className="w-full bg-[#0A0A0B] border border-white/20 text-white px-4 py-3 focus:border-[#00E5FF] outline-none [color-scheme:dark]"
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-gray-400 font-semibold block mb-2">Durasi (Jam)</label>
                    <select
                      value={duration}
                      onChange={(e) => setDuration(Number(e.target.value))}
                      data-testid="booking-duration-select"
                      className="w-full bg-[#0A0A0B] border border-white/20 text-white px-4 py-3 focus:border-[#00E5FF] outline-none"
                    >
                      {[1, 2, 3, 4, 5, 6, 8, 10, 12].map((h) => (
                        <option key={h} value={h}>{h} jam</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="mt-6">
                  <label className="text-xs uppercase tracking-widest text-gray-400 font-semibold block mb-3">Pilih Jam Mulai</label>
                  <div className="grid grid-cols-4 md:grid-cols-8 gap-2">
                    {Array.from({ length: 15 }, (_, i) => i + 9).map((h) => {
                      const label = `${h.toString().padStart(2, "0")}:00`;
                      const isBooked = bookedHours.includes(h);
                      const conflict = Array.from({ length: duration }, (_, k) => h + k).some((x) => bookedHours.includes(x));
                      const disabled = isBooked || conflict;
                      const selected = startTime === label;
                      return (
                        <button
                          key={h}
                          disabled={disabled}
                          onClick={() => setStartTime(label)}
                          data-testid={`slot-${label}`}
                          className={`py-2.5 text-sm font-semibold border transition-colors ${
                            selected ? "bg-[#00E5FF] text-black border-[#00E5FF]" :
                            disabled ? "bg-white/5 text-gray-600 border-white/5 cursor-not-allowed" :
                            "border-white/10 text-white hover:border-[#00E5FF]"
                          }`}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                  <div className="mt-3 text-xs text-gray-500">Slot berwarna abu-abu sudah dibooking.</div>
                </div>
                {selectedUnit && duration && (
                  <div className="mt-6 p-4 bg-[#00E5FF]/5 border border-[#00E5FF]/30 flex justify-between">
                    <span className="text-sm text-gray-300">Total: <span className="font-bold text-white">{selectedUnit.name}</span> × {duration} jam</span>
                    <span className="font-heading font-black text-[#00E5FF] text-lg">{formatIDR(totalPrice)}</span>
                  </div>
                )}
              </motion.div>
            )}

            {step === 2 && (
              <motion.div key="s2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <h3 className="font-heading text-2xl font-bold text-white mb-6">Data Pemesan</h3>
                <div className="grid grid-cols-1 gap-5 max-w-xl">
                  <div>
                    <label className="text-xs uppercase tracking-widest text-gray-400 font-semibold block mb-2">Nama Lengkap</label>
                    <input
                      value={customerName}
                      onChange={(e) => setCustomerName(e.target.value)}
                      data-testid="customer-name-input"
                      placeholder="Nama sesuai identitas"
                      className="w-full bg-[#0A0A0B] border border-white/20 text-white px-4 py-3 focus:border-[#00E5FF] outline-none"
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-gray-400 font-semibold block mb-2">Nomor WhatsApp Aktif</label>
                    <input
                      value={whatsapp}
                      onChange={(e) => setWhatsapp(e.target.value)}
                      data-testid="customer-wa-input"
                      placeholder="081234567890"
                      className="w-full bg-[#0A0A0B] border border-white/20 text-white px-4 py-3 focus:border-[#00E5FF] outline-none"
                    />
                    <div className="text-xs text-gray-500 mt-1">Kami akan kirim konfirmasi booking ke nomor ini.</div>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-gray-400 font-semibold block mb-2">Catatan (opsional)</label>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={3}
                      data-testid="customer-notes-input"
                      placeholder="Request game tertentu, dsb"
                      className="w-full bg-[#0A0A0B] border border-white/20 text-white px-4 py-3 focus:border-[#00E5FF] outline-none resize-none"
                    />
                  </div>

                  <div>
                    <label className="text-xs uppercase tracking-widest text-gray-400 font-semibold block mb-2">Kode Kupon (opsional)</label>
                    {!appliedCoupon ? (
                      <div className="flex gap-2">
                        <input
                          value={couponInput}
                          onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                          data-testid="coupon-input"
                          placeholder="MISAL: HEMAT10"
                          className="flex-1 bg-[#0A0A0B] border border-white/20 text-white px-4 py-3 focus:border-[#00E5FF] outline-none font-mono tracking-wider"
                        />
                        <button
                          type="button"
                          onClick={validateCoupon}
                          disabled={validatingCoupon}
                          data-testid="apply-coupon-btn"
                          className="px-5 border border-[#00E5FF] text-[#00E5FF] font-bold text-xs uppercase tracking-widest hover:bg-[#00E5FF] hover:text-black transition-colors disabled:opacity-50"
                        >
                          {validatingCoupon ? "..." : "Pakai"}
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between p-3 border border-[#00E5FF]/40 bg-[#00E5FF]/10" data-testid="coupon-applied">
                        <div>
                          <div className="text-xs uppercase tracking-widest text-[#00E5FF] font-bold">✓ {appliedCoupon.code}</div>
                          <div className="text-xs text-gray-300 mt-0.5">Hemat {formatIDR(appliedCoupon.discount)}</div>
                        </div>
                        <button
                          type="button"
                          onClick={removeCoupon}
                          data-testid="remove-coupon-btn"
                          className="text-xs text-[#FF0055] hover:underline uppercase tracking-widest"
                        >
                          Hapus
                        </button>
                      </div>
                    )}
                  </div>

                  {selectedUnit && (
                    <div className="p-4 border border-white/10 bg-[#0A0A0B] space-y-1.5 text-sm" data-testid="step2-total">
                      <div className="flex justify-between text-gray-400"><span>Subtotal</span><span className="text-white">{formatIDR(totalPrice)}</span></div>
                      {appliedCoupon && (
                        <div className="flex justify-between text-gray-400"><span>Diskon ({appliedCoupon.code})</span><span className="text-[#00E5FF]">- {formatIDR(discount)}</span></div>
                      )}
                      <div className="flex justify-between pt-2 border-t border-white/10">
                        <span className="text-gray-300 font-semibold">Total Bayar</span>
                        <span className="font-heading font-black text-[#00E5FF] text-lg">{formatIDR(totalAfterDiscount)}</span>
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {step === 3 && booking && (
              <motion.div key="s3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <h3 className="font-heading text-2xl font-bold text-white mb-2">Pembayaran</h3>
                <p className="text-sm text-gray-400 mb-6">Transfer sesuai nominal, lalu upload bukti transfer.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="border border-white/10 bg-[#0A0A0B] p-5">
                      <div className="text-xs uppercase tracking-widest text-[#00E5FF] mb-3">Ringkasan</div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between"><span className="text-gray-400">Unit</span><span className="text-white">{booking.unit_name}</span></div>
                        <div className="flex justify-between"><span className="text-gray-400">Tanggal</span><span className="text-white">{booking.booking_date}</span></div>
                        <div className="flex justify-between"><span className="text-gray-400">Jam</span><span className="text-white">{booking.start_time} ({booking.duration_hours}j)</span></div>
                        <div className="flex justify-between"><span className="text-gray-400">Nama</span><span className="text-white">{booking.customer_name}</span></div>
                        {booking.subtotal > 0 && (
                          <div className="flex justify-between"><span className="text-gray-400">Subtotal</span><span className="text-white">{formatIDR(booking.subtotal)}</span></div>
                        )}
                        {booking.discount > 0 && (
                          <div className="flex justify-between"><span className="text-gray-400">Diskon ({booking.coupon_code})</span><span className="text-[#00E5FF]">- {formatIDR(booking.discount)}</span></div>
                        )}
                        <div className="border-t border-white/10 pt-3 mt-3 flex justify-between">
                          <span className="text-gray-400">Total</span>
                          <span className="font-heading font-black text-[#00E5FF] text-lg">{formatIDR(booking.total_price)}</span>
                        </div>
                      </div>
                    </div>

                    <div className="border border-white/10 bg-[#0A0A0B] p-5">
                      <div className="text-xs uppercase tracking-widest text-[#00E5FF] mb-3">Metode Pembayaran</div>
                      {settings?.qris_image_base64 && (
                        <div className="mb-4">
                          <div className="text-xs text-gray-400 mb-2">Scan QRIS:</div>
                          <img src={settings.qris_image_base64} alt="QRIS" className="w-40 h-40 object-contain bg-white p-2" />
                        </div>
                      )}
                      <div className="space-y-2">
                        {settings?.ewallets?.map((w) => (
                          <div key={w.name} className="flex justify-between items-center p-2 bg-white/5 text-sm">
                            <div>
                              <div className="text-white font-semibold">{w.name}</div>
                              <div className="text-xs text-gray-400">a.n {w.holder}</div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-white font-mono text-sm">{w.number}</span>
                              <button onClick={() => copy(w.number)} className="text-[#00E5FF] hover:text-white" data-testid={`copy-${w.name}`}>
                                <Copy size={14} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="border border-[#00E5FF]/30 bg-[#00E5FF]/5 p-5">
                    <div className="text-xs uppercase tracking-widest text-[#00E5FF] mb-3">Upload Bukti Transfer</div>
                    <label className="block cursor-pointer" data-testid="proof-upload-label">
                      <input type="file" accept="image/*" onChange={handleFileChange} className="hidden" data-testid="proof-upload-input" />
                      <div className="border-2 border-dashed border-white/20 hover:border-[#00E5FF] p-6 text-center transition-colors">
                        {proofPreview ? (
                          <img src={proofPreview} alt="Preview" className="max-h-64 mx-auto" />
                        ) : (
                          <div className="py-8">
                            <Upload size={32} className="mx-auto text-gray-500 mb-3" />
                            <div className="text-sm text-gray-400">Klik untuk pilih file (max 3MB)</div>
                          </div>
                        )}
                      </div>
                    </label>
                    <button
                      onClick={handleUploadProof}
                      disabled={!proofPreview || submitting}
                      data-testid="submit-proof-btn"
                      className="w-full mt-4 bg-[#00E5FF] text-black font-bold py-3 uppercase tracking-wider text-sm hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {submitting ? "Mengirim..." : "Kirim Pembayaran"}
                    </button>
                  </div>
                </div>
              </motion.div>
            )}

            {step === 4 && booking && (
              <motion.div key="s4" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-10">
                <CheckCircle2 size={72} className="mx-auto text-[#00E5FF]" />
                <h3 className="font-heading text-3xl font-black text-white mt-6">BOOKING TERKIRIM!</h3>
                <p className="text-gray-400 mt-3 max-w-md mx-auto">
                  Booking kamu sedang diverifikasi admin. Kamu akan menerima notifikasi WhatsApp setelah admin mengkonfirmasi pembayaran.
                </p>
                <div className="mt-8 inline-block border border-white/10 bg-[#0A0A0B] p-6 text-left">
                  <div className="text-xs uppercase tracking-widest text-[#00E5FF] mb-3">ID Booking</div>
                  <div className="font-mono text-white text-sm break-all">{booking.id}</div>
                  <div className="mt-4 pt-4 border-t border-white/5 text-sm">
                    Status: <span className="text-yellow-500 font-bold">Menunggu Konfirmasi</span>
                  </div>
                </div>
                <div className="mt-8">
                  <button
                    onClick={() => nav("/")}
                    data-testid="back-home-btn"
                    className="px-8 py-3 border border-white/20 text-white text-sm uppercase tracking-wider font-semibold hover:border-[#00E5FF] hover:text-[#00E5FF] transition-colors"
                  >
                    Kembali ke Beranda
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {step < 3 && (
            <div className="mt-10 flex justify-between pt-6 border-t border-white/5">
              <button
                onClick={() => setStep(Math.max(0, step - 1))}
                disabled={step === 0}
                data-testid="prev-step-btn"
                className="inline-flex items-center gap-2 px-6 py-3 border border-white/20 text-white text-sm uppercase tracking-wider font-semibold hover:border-white/40 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft size={16} /> Kembali
              </button>
              {step === 2 ? (
                <button
                  onClick={handleCreateBooking}
                  disabled={!canProceed() || submitting}
                  data-testid="submit-booking-btn"
                  className="inline-flex items-center gap-2 px-8 py-3 bg-[#00E5FF] text-black text-sm uppercase tracking-wider font-bold hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {submitting ? "Memproses..." : "Lanjut Bayar"} <ChevronRight size={16} />
                </button>
              ) : (
                <button
                  onClick={() => setStep(step + 1)}
                  disabled={!canProceed()}
                  data-testid="next-step-btn"
                  className="inline-flex items-center gap-2 px-8 py-3 bg-[#00E5FF] text-black text-sm uppercase tracking-wider font-bold hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Lanjut <ChevronRight size={16} />
                </button>
              )}
            </div>
          )}
        </div>

        {/* Bottom Booking Ad - placed AFTER the wizard box so it never overlaps action buttons */}
        {settings && settings.ad_slot_booking_bottom && settings.ad_booking_bottom_enabled !== false && (
          <div className="mt-8 py-4 border-t border-white/5" data-testid="ad-booking-bottom-container">
            <AdSlot
              snippet={settings.ad_slot_booking_bottom}
              publisherId={settings.adsense_publisher_id}
              label="Sponsored"
              slotKey="booking_bottom"
            />
          </div>
        )}
      </div>
    </div>
  );
}
